import {
  useContext, useEffect, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useConfiguration } from '@printdeal/configuration/react';
import { ErrorManagement } from '@printdeal/error-management/react';
import { ZendeskChatProps, ZendeskChatOutput } from './chatTypes';
import {
  canUseDOM,
  handleUpdateUser,
  handleStatus,
  handleUpdateLocale,
} from './chat-helper';
import { registerZendeskChatListeners } from './zendesk-chat-session';
import ChatContext from './ChatContext';
import { ServiceFactory } from '../../../services/ServiceFactory';
import { selectors as authSelectors } from '../../../store/slices/authSlice';
import { RootState } from '../../../store';

// The main useZendeskChat hook
const useZendeskChat = ({
  onLoading = () => {},
  onOpen = () => {},
  onClose = () => {},
}: ZendeskChatProps): ZendeskChatOutput => {
  // Reference to the script element
  const {
    setChatStatus,
    chatConfig: { zendeskKey },
    locale,
    identity: { name, email },
  } = useContext(ChatContext);
  const configuration = useConfiguration();
  const { storeKey } = configuration;
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const isLoggedIn = useSelector((state: RootState) => authSelectors.isLoggedIn(state));

  const getChatUserToken = async () => {
    if (!isLoggedIn) return '';
    try {
      const zendeskService = ServiceFactory.createZendeskTicketService();
      const { data: token } = await zendeskService.authenticateMessenger();
      return token;
    } catch (error) {
      ErrorManagement.exception({
        exception: 'Fetching Zendesk messenger user token failed',
        extra: {
          error: JSON.stringify(error),
        },
        tags: {
          fileLocation: 'useZendeskChat',
        },
        fingerprint: ['fetching-zendesk-messenger-user-token-failed'],
      });
      console.error('Fetching Zendesk messenger user token failed with error:', error);
      return '';
    }
  };

  // Callback when the Zendesk script is loaded
  const onScriptLoaded = async () => {
    onLoading();
    onOpen();
    await registerZendeskChatListeners();
    handleUpdateLocale(locale);
    if (isLoggedIn) {
      window.zE('messenger', 'hide');
    }
    window.zE('messenger:set', 'conversationTags', [storeKey]);
    const zendeskUserToken = await getChatUserToken();

    setChatStatus('open');

    if (isLoggedIn && zendeskUserToken) {
      window.zE('messenger', 'loginUser', async (done: (token: string) => void) => {
        done(zendeskUserToken);
      }, (error: Error) => {
        if (!error) {
          window.zE('messenger', 'show');
          handleStatus('open');
          return;
        }
        ErrorManagement.exception({
          exception: 'Login Zendesk messenger failed',
          extra: {
            error: JSON.stringify(error),
          },
          tags: {
            fileLocation: 'useZendeskChat',
          },
          fingerprint: ['login-zendesk-messenger-failed'],
        });
      });
    } else {
      window.zE('messenger', 'show');
      handleStatus('open');
    }
  };

  // Insert the Zendesk script
  const insertScript = () => {
    if (canUseDOM() && !window.zE) {
      scriptRef.current = document.createElement('script');
      scriptRef.current.defer = true;
      scriptRef.current.id = 'ze-snippet';
      scriptRef.current.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
      scriptRef.current.addEventListener('load', onScriptLoaded);
      document.body.appendChild(scriptRef.current);
    }
  };

  // Update the user for zendesk chat
  useEffect(() => {
    handleUpdateUser(name, email);
  }, [name, email]);

  // Exposed methods for opening and closing Zendesk chat
  const openChat = () => {
    setChatStatus('loading');
    insertScript();
  };
  const closeChat = () => {
    handleStatus('close');
    onClose();
  };

  // Return the exposed methods
  return { openChat, closeChat };
};

export default useZendeskChat;
